import { useState } from 'react';
import type { UseQueryOptions } from 'react-query';
import { useQueries } from 'react-query';
import type {
  Deduction,
  SubscriptionChangePriceSummary,
} from '@hotjar/webapp/billing/subscription';
import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import type { Currency } from '@hotjar/account';
import { useSettings } from '@hotjar/webapp/hooks/useSettings';
import type { CostSpecDTO } from '@hotjar/webapp/domains/billing';
import { useOwnedAccount } from '@hotjar/account';

import type { BillingCycleDisplayCosts } from './displayCosts';
import { getBillingCycleDisplayCosts, getDisclaimerDisplayCosts } from './displayCosts';

export interface UseDisplayCostsResponse {
  yearlyCosts: BillingCycleDisplayCosts | undefined;
  monthlyCosts: BillingCycleDisplayCosts | undefined;
  disclaimerDisplayCosts: ReturnType<typeof getDisclaimerDisplayCosts> | undefined;
}

export function useDisplayCosts(costSpecs: CostSpecDTO[]): UseDisplayCostsResponse {
  const { data: settings } = useSettings();
  const { data: account } = useOwnedAccount();
  const currencySymbols = settings?.currency_symbols;

  const currencySymbol = currencySymbols?.[account?.currency as Currency];

  const [yearlyCosts, setYearlyCosts] = useState<BillingCycleDisplayCosts>();
  const [monthlyCosts, setMonthlyCosts] = useState<BillingCycleDisplayCosts>();
  const [yearlyPriceSummary, setYearlyPriceSummary] = useState<
    SubscriptionChangePriceSummary | undefined
  >();

  const getBaseQueryOptions = (
    cycle: IBillingCycle,
  ): Pick<UseQueryOptions, 'queryFn' | 'queryKey' | 'notifyOnChangeProps'> => ({
    queryKey: cycle,
    queryFn: () => subscriptionAPI.preview({ id: account?.id }, costSpecs, cycle),
    notifyOnChangeProps: ['data', 'status', 'error'],
  });

  useQueries([
    {
      ...getBaseQueryOptions('yearly_recurring'),
      onSuccess: (data) => {
        const yearly = data as SubscriptionChangePriceSummary;
        setYearlyPriceSummary(yearly);
        const { total, deductions } = yearly.charge;
        const { discountPercentage } = yearly.future;

        const hasOneTimeDeduction =
          total.gt('0') &&
          deductions.find(
            (deduction: Deduction) =>
              deduction.application === 'ONCE' && deduction.percentage > '0',
          );
        const futureSavingsPercentage = hasOneTimeDeduction ? discountPercentage : null;

        setYearlyCosts(
          getBillingCycleDisplayCosts({
            summary: yearly.future,
            duration: 12,
            futureSavingsPercentage,
            currencySymbol,
          }),
        );
      },
    },
    {
      ...getBaseQueryOptions('monthly'),
      onSuccess: (data) => {
        const monthly = data as SubscriptionChangePriceSummary;
        setMonthlyCosts(
          getBillingCycleDisplayCosts({
            summary: monthly.future,
            duration: 1,
            futureSavingsPercentage: null,
            currencySymbol,
          }),
        );
      },
    },
  ]);

  const disclaimerDisplayCosts = yearlyPriceSummary
    ? getDisclaimerDisplayCosts({
        currentTotal: yearlyPriceSummary?.charge.total,
        futureTotal: yearlyPriceSummary?.future.total,
        isProrated: yearlyPriceSummary?.charge.isProrated,
        currencySymbol,
      })
    : undefined;

  return {
    yearlyCosts,
    monthlyCosts,
    disclaimerDisplayCosts,
  };
}
